import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    CircularProgress,
    makeStyles
} from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import EmailEditor from "../../components/EmailEditor";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    paper: {
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)",
    },
    dialogContent: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        padding: "0"
    },
    editorContainer: {
        flex: 1,
        minHeight: "700px",
        border: "1px solid #ccc",
        marginTop: theme.spacing(2)
    }
}));

const TemplateSchema = Yup.object().shape({
    name: Yup.string()
        .min(2, "Too Short!")
        .max(50, "Too Long!")
        .required("Required"),
});

const EmailTemplateModal = ({ open, onClose, emailTemplateId }) => {
    const classes = useStyles();

    const initialState = {
        name: "",
        designJson: null,
        body: "",
        header: "",
        footer: ""
    };

    const [initialValues, setInitialValues] = useState(initialState);
    const [loadingTemplate, setLoadingTemplate] = useState(false);

    useEffect(() => {
        const fetchTemplate = async () => {
            if (!emailTemplateId) return;
            setLoadingTemplate(true);
            try {
                const { data } = await api.get(`/email-templates/${emailTemplateId}`);
                setInitialValues(prevState => {
                    return { ...prevState, ...data };
                });
            } catch (err) {
                toast.error(err);
            }
            setLoadingTemplate(false);
        };

        fetchTemplate();
    }, [emailTemplateId, open]);

    const handleClose = () => {
        onClose();
        setInitialValues(initialState);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xl"
            fullWidth
            scroll="paper"
            PaperProps={{ className: classes.paper }}
        >
            <DialogTitle id="form-dialog-title">
                {emailTemplateId
                    ? `${i18n.t("emailTemplates.title.edit")}`
                    : `${i18n.t("emailTemplates.title.add")}`}
            </DialogTitle>

            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                validationSchema={TemplateSchema}
                onSubmit={(values) => {
                    // Placeholder for formik submit, actual save is handled by handleSaveDesign
                }}
            >
                {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => {

                    const handleSaveDesign = async () => {
                        if (!values.name) {
                            toast.error("Please enter a name for the template");
                            return;
                        }

                        // We use values.body directly now, updated by SimpleHtmlEditor
                        const templateData = {
                            ...values,
                            designJson: null // No longer using GrapesJS design
                        };

                        try {
                            if (emailTemplateId) {
                                await api.put(`/email-templates/${emailTemplateId}`, templateData);
                            } else {
                                await api.post("/email-templates", templateData);
                            }
                            toast.success(i18n.t("emailTemplates.toasts.success"));
                            handleClose();
                        } catch (err) {
                            toast.error(err);
                        }
                    };

                    return (
                        <div className={classes.dialogContent}>
                            <div style={{ padding: '16px 24px' }}>
                                <Field
                                    as={TextField}
                                    label={i18n.t("emailTemplates.form.name")}
                                    name="name"
                                    fullWidth
                                    error={touched.name && Boolean(errors.name)}
                                    helperText={touched.name && errors.name}
                                    variant="outlined"
                                />
                            </div>

                            <div className={classes.editorContainer}>
                                {loadingTemplate ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '500px' }}>
                                        <CircularProgress />
                                    </div>
                                ) : (
                                    <EmailEditor
                                        key={emailTemplateId || 'new-template'}
                                        value={values.body || ''}
                                        onChange={(content) => setFieldValue('body', content)}
                                    />
                                )}
                            </div>

                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("emailTemplates.buttons.cancel")}
                                </Button>
                                <Button
                                    onClick={handleSaveDesign}
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                >
                                    {i18n.t("emailTemplates.buttons.save")}
                                </Button>
                            </DialogActions>
                        </div>
                    );
                }}
            </Formik>
        </Dialog >
    );
};

export default EmailTemplateModal;
